import { useNavigate, useParams } from "react-router";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ErrorState } from "../../components/ui/ErrorState";
import { Loading } from "../../components/ui/Page";
import { useAction } from "../../hooks/useAction";
import { useResource } from "../../hooks/useResource";
import { membersApi } from "../../lib/api/members";
import { formatDate } from "../../lib/format";
import { useMe, useSession } from "../../app/session";
import { rememberWorkspace } from "../../app/workspace";
import { AuthLayout } from "./AuthLayout";

/** The link in an invitation email: /invite/:id. The invited email must be the signed-in account. */
export function InvitePage() {
  const { id = "" } = useParams();
  const me = useMe();
  const { refresh, signOut } = useSession();
  const navigate = useNavigate();
  const inv = useResource((signal) => membersApi.invitation(id, signal), [id]);

  const accept = useAction(async () => {
    await membersApi.accept(id);
    rememberWorkspace(inv.data!.organizationId);
    await refresh();
    navigate("/streams", { replace: true });
  });
  const reject = useAction(async () => { await membersApi.reject(id); navigate("/streams", { replace: true }); });

  return (
    <AuthLayout title="Join a workspace">
      {inv.loading && <Loading />}
      {inv.error !== undefined && (
        <div className="flex flex-col gap-3">
          <ErrorState error={inv.error} />
          <p className="text-muted">
            You're signed in as <strong>{me.user.email}</strong>. If the invitation went to another email, sign in with that one.
          </p>
          <Button onClick={() => void signOut()}>sign out</Button>
        </div>
      )}
      {inv.data && (
        <div className="flex flex-col gap-4.5">
          <p>
            <strong>{inv.data.inviterEmail}</strong> invited you to <strong>{inv.data.organizationName}</strong> as{" "}
            <strong>{inv.data.role}</strong>.
          </p>
          <p className="text-muted">// expires {formatDate(inv.data.expiresAt)}</p>
          {inv.data.status !== "pending" && <Banner tone="warn">This invitation was already {inv.data.status}.</Banner>}
          {accept.error && <Banner tone="bad">{accept.error}</Banner>}
          {reject.error && <Banner tone="bad">{reject.error}</Banner>}
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" disabled={inv.data.status !== "pending"} pending={accept.pending} onClick={() => void accept.run()}>
              accept →
            </Button>
            <Button disabled={inv.data.status !== "pending"} pending={reject.pending} onClick={() => void reject.run()}>decline</Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
